import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Laptop, Wifi, Download, Gift, BookOpen, ArrowRight, History } from 'lucide-react';
import { Button } from '../components/ui/button';
import BookCard from '../components/BookCard';
import { BOOKS } from '../mock/mock';
import { PageHero } from './BuyABook';

const ReadOnline = () => {
  const freeBooks = BOOKS.filter((b) => b.free && b.pdf);
  const [resume, setResume] = useState(null);

  useEffect(() => {
    try {
      const saved = localStorage.getItem('lr_reading');
      if (saved) setResume(JSON.parse(saved));
    } catch {
      /* ignore */
    }
  }, []);

  return (
    <div className="fade-in">
      <PageHero
        title="Read Online — Free"
        subtitle="Read the author's books free, right in your browser, or download them to keep. No payment required."
      />

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
        {resume && (
          <div className="mb-12 bg-brand-ink rounded-2xl p-6 flex flex-col sm:flex-row items-center gap-5 shadow-lg">
            <img src={resume.front} alt={resume.title} className="w-16 h-24 object-cover rounded-md shadow-md" />
            <div className="flex-1 text-center sm:text-left">
              <div className="flex items-center gap-2 justify-center sm:justify-start text-brand-gold text-xs uppercase tracking-widest">
                <History className="h-3.5 w-3.5" /> Continue Reading
              </div>
              <h3 className="font-serif text-2xl font-bold text-cream mt-1">{resume.title}</h3>
              <p className="text-white/60 text-sm italic">{resume.subtitle}</p>
            </div>
            <Link to={`/read/${resume.id}`}>
              <Button className="bg-brand-gold hover:bg-brand-brass text-brand-ink hover:text-white rounded-full h-11 px-7 gap-2">
                <BookOpen className="h-4 w-4" /> Resume
              </Button>
            </Link>
          </div>
        )}

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {[
            { icon: Gift, t: 'Completely Free', d: 'Every digital edition is free to read.' },
            { icon: Laptop, t: 'Any Device', d: 'Read on phone, tablet, or desktop.' },
            { icon: Wifi, t: 'Read In-Browser', d: 'Open and read instantly online.' },
            { icon: Download, t: 'Download to Keep', d: 'Save the PDF and read offline.' },
          ].map((f) => (
            <div key={f.t} className="bg-white rounded-2xl p-6 shadow-md text-center">
              <div className="h-12 w-12 rounded-xl bg-sand flex items-center justify-center mx-auto mb-4">
                <f.icon className="h-6 w-6 text-brand-brass" />
              </div>
              <h3 className="font-serif text-lg font-bold text-brand-ink">{f.t}</h3>
              <p className="text-sm text-brand-brown/70 mt-1">{f.d}</p>
            </div>
          ))}
        </div>

        <div className="text-center mb-10">
          <h2 className="font-serif text-3xl font-bold text-brand-ink">Free Digital Library</h2>
          <div className="section-divider mx-auto mt-4" />
          <p className="text-brand-brown/80 mt-4 max-w-xl mx-auto">
            {freeBooks.length} titles available to read online or download at no cost.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10">
          {freeBooks.map((b) => (
            <BookCard key={b.id} book={b} />
          ))}
        </div>

        <div className="mt-16 bg-sand/60 rounded-3xl p-10 text-center">
          <BookOpen className="h-10 w-10 text-brand-brass mx-auto mb-3" />
          <h3 className="font-serif text-2xl font-bold text-brand-ink">Love a book? Own the print edition</h3>
          <p className="text-brand-brown/80 mt-2 max-w-lg mx-auto">
            Enjoy reading for free — and if a story stays with you, order a beautiful physical copy to keep or gift.
          </p>
          <Link to="/buy">
            <Button className="mt-6 bg-brand-ink hover:bg-brand-brown text-cream rounded-full h-12 px-8 gap-2">
              Shop Physical Copies <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ReadOnline;
